import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  FaUser, FaSearch, FaMapMarkerAlt, FaEnvelope,
  FaPhone, FaCalendarAlt, FaIdCard, FaWallet,
  FaEdit, FaTrash, FaTimes, FaSave, FaFilter, FaRedo, FaChevronLeft, FaChevronRight
} from "react-icons/fa";
import { toast } from "react-toastify";

const AllUser = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Edit Modal State
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    email: "",
    mobile: "",
    role: "Advertiser",
    gender: "male",
    state: "",
    city: "",
    pincode: "",
    residenceaddress: "",
    password: ""
  });
  const [updateLoading, setUpdateLoading] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50; // Increased as requested by user

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/super/admin/get/total/users/`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const data = response.data.users || response.data.data || response.data || [];

      if (Array.isArray(data)) {
        setUsers(data);
      } else {
        setUsers([]);
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
        err.message ||
        "Failed to fetch users"
      );
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const getStats = () => {
    const total = users.length;
    const reporters = users.filter(u => u.role === "Reporter").length;
    const influencers = users.filter(u => u.role === "Influencer").length;
    const advertisers = users.filter(u => u.role === "Advertiser").length;
    const nullRole = users.filter(u => !u.role || u.role === "null" || u.role === "").length;

    return { total, reporters, influencers, advertisers, nullRole };
  };

  const stats = getStats();
  const [filterRole, setFilterRole] = useState("All");

  useEffect(() => {
    setCurrentPage(1);
  }, [filterRole, searchTerm]);

  // Filter logic
  const filteredUsers = users.filter((user) => {
    const term = searchTerm.toLowerCase();
    const matchesSearch = (
      (user.name && user.name.toLowerCase().includes(term)) ||
      (user.email && user.email.toLowerCase().includes(term)) ||
      (user.mobile && user.mobile.toString().toLowerCase().includes(term)) ||
      (user.city && user.city.toLowerCase().includes(term)) ||
      (user.state && user.state.toLowerCase().includes(term)) ||
      (user.iinsafId && user.iinsafId.toLowerCase().includes(term))
    );

    if (!matchesSearch) return false;
    if (filterRole === "All") return true;
    if (filterRole === "Reporter") return user.role === "Reporter";
    if (filterRole === "Influencer") return user.role === "Influencer";
    if (filterRole === "Advertiser") return user.role === "Advertiser";
    if (filterRole === "Null") return !user.role || user.role === "null" || user.role === "";

    return user.role === filterRole;
  }).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  const handleEditClick = (user) => {
    setEditingUser(user);
    setEditForm({
      name: user.name || "",
      email: user.email || "",
      mobile: user.mobile || "",
      role: user.role || "Advertiser",
      gender: user.gender || "male",
      state: user.state || "",
      city: user.city || "",
      pincode: user.pincode || "",
      residenceaddress: user.residenceaddress || "",
      password: ""
    });
    setIsEditModalOpen(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      setUpdateLoading(true);
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/admin/user/update/${editingUser._id}`,
        editForm,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.success) {
        toast.success("User updated successfully");
        setIsEditModalOpen(false);
        fetchUsers();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update user");
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleDeleteUser = async (id) => {
    if (window.confirm("Are you sure you want to delete this user? This will also delete their wallet.")) {
      try {
        const response = await axios.delete(
          `${process.env.REACT_APP_API_BASE_URL}/admin/user/delete/${id}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (response.data.success) {
          toast.success("User deleted successfully");
          fetchUsers();
        }
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to delete user");
      }
    }
  };

  const roleConfigs = {
    Reporter: { color: "purple", icon: <FaIdCard />, bg: "bg-purple-50", text: "text-purple-600", border: "border-purple-200" },
    Influencer: { color: "pink", icon: <FaUser />, bg: "bg-pink-50", text: "text-pink-600", border: "border-pink-200" },
    Advertiser: { color: "emerald", icon: <FaIdCard />, bg: "bg-emerald-50", text: "text-emerald-600", border: "border-emerald-200" },
    All: { color: "blue", icon: <FaUser />, bg: "bg-blue-50", text: "text-blue-600", border: "border-blue-200" },
    Null: { color: "orange", icon: <FaUser />, bg: "bg-orange-50", text: "text-orange-600", border: "border-orange-200" }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] p-4 md:p-8 font-sans transition-all duration-300">

      {/* Background Decorative Elements */}
      <div className="fixed top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 z-50"></div>

      {/* Page Header - Compact */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-black text-[#1e293b] tracking-tight">
            User Ecosystem
          </h1>
          <p className="text-[#64748b] text-[10px] font-bold uppercase tracking-widest">
            Registry Control Panel
          </p>
        </div>
        <button
          onClick={fetchUsers}
          className="flex items-center gap-2 px-4 py-2 bg-white text-[#475569] border border-[#e2e8f0] rounded-xl shadow-sm hover:bg-gray-50 transition-all font-bold text-xs"
        >
          <FaRedo className={`${loading ? 'animate-spin' : ''}`} />
          Sync
        </button>
      </div>

      {/* Stats Dashboard - Filled & Compact */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-8">
        {Object.entries(roleConfigs).map(([role, config]) => (
          <div
            key={role}
            onClick={() => setFilterRole(role)}
            className={`relative cursor-pointer p-3 rounded-2xl border transition-all ${filterRole === role
              ? `${config.bg} ${config.border} border-2 shadow-sm`
              : "bg-gray-50 border-transparent hover:bg-gray-100"
              }`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 ${filterRole === role ? 'bg-white' : config.bg} rounded-lg flex items-center justify-center ${config.text} text-sm`}>
                {config.icon}
              </div>
              <div>
                <div className="text-[#94a3b8] text-[9px] font-black uppercase tracking-wider">
                  {role === 'Null' ? 'General' : role}
                </div>
                <div className={`text-lg font-black ${filterRole === role ? 'text-[#0f172a]' : 'text-[#334155]'}`}>
                  {role === 'All' ? stats.total : stats[role.toLowerCase() === 'null' ? 'nullRole' : role.toLowerCase() + 's'] || stats[role.toLowerCase() === 'null' ? 'nullRole' : role.toLowerCase()]}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Main Content Area */}
      <div className="bg-white rounded-[2.5rem] shadow-2xl shadow-blue-500/5 border border-[#f1f5f9] overflow-hidden">

        {/* Table Content Toolbar */}
        <div className="p-6 md:p-8 border-b border-[#f1f5f9] flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center text-white shadow-lg shadow-blue-500/30">
              <FaFilter />
            </div>
            <div>
              <h3 className="text-xl font-bold text-[#1e293b]">Directory </h3>
              <p className="text-xs text-[#94a3b8] font-bold">{filteredUsers.length} active users listed</p>
            </div>
          </div>

          <div className="relative w-full md:w-96 group">
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-[#94a3b8] group-focus-within:text-blue-500 transition-colors" />
            <input
              type="text"
              placeholder="Search by name, email, ID..."
              className="w-full pl-12 pr-4 py-3.5 bg-[#f8fafc] border-transparent rounded-2xl outline-none focus:ring-4 focus:ring-blue-500/10 focus:bg-white focus:border-blue-200 transition-all text-sm font-medium text-[#334155]"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Improved Table Design */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-[#f8fafc]">
                <th className="px-6 py-4 text-left text-[10px] font-black text-[#64748b] uppercase tracking-widest border-b border-[#f1f5f9]">#</th>
                <th className="px-8 py-5 text-left text-xs font-black text-[#64748b] uppercase tracking-widest">Profile</th>
                <th className="px-8 py-5 text-left text-xs font-black text-[#64748b] uppercase tracking-widest text-center">Identity</th>
                <th className="px-8 py-5 text-left text-xs font-black text-[#64748b] uppercase tracking-widest">Financials</th>
                <th className="px-8 py-5 text-left text-xs font-black text-[#64748b] uppercase tracking-widest">Contact details</th>
                <th className="px-8 py-5 text-center text-xs font-black text-[#64748b] uppercase tracking-widest">Operations</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#f1f5f9]">
              {loading ? (
                Array(5).fill(0).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td colSpan="5" className="px-8 py-8"><div className="h-10 bg-gray-100 rounded-2xl w-full"></div></td>
                  </tr>
                ))
              ) : currentItems.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-8 py-20 text-center">
                    <div className="flex flex-col items-center gap-4">
                      <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center text-[#cbd5e1] text-4xl">
                        <FaSearch />
                      </div>
                      <p className="text-[#94a3b8] font-bold">No explorers found match your criteria</p>
                    </div>
                  </td>
                </tr>
              ) : (
                currentItems.map((user, idx) => (
                  <tr key={user._id} className="hover:bg-gray-50 transition-all duration-150">
                    <td className="px-6 py-3 whitespace-nowrap text-[10px] font-black text-gray-400">
                      {(currentPage - 1) * itemsPerPage + idx + 1}
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          {user.profileImage ? (
                            <img
                              src={user.profileImage}
                              alt={user.name}
                              className="w-10 h-10 rounded-xl object-cover border border-gray-100 shadow-sm"
                            />
                          ) : (
                            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br from-${roleConfigs[user.role || 'Null'].color}-500 to-${roleConfigs[user.role || 'Null'].color}-700 flex items-center justify-center text-white text-sm font-black shadow-md shadow-${roleConfigs[user.role || 'Null'].color}-500/20`}>
                              {user.name ? user.name.charAt(0).toUpperCase() : <FaUser />}
                            </div>
                          )}
                          <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-white border-2 border-white rounded-full flex items-center justify-center">
                            <div className="w-full h-full rounded-full bg-emerald-500"></div>
                          </div>
                        </div>
                        <div>
                          <p className="text-sm font-bold text-[#1e293b] tracking-tight truncate max-w-[150px]">{user.name || "Anonymous User"}</p>
                          <p className="text-[9px] text-[#94a3b8] font-black uppercase tracking-widest">Joined {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}</p>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-3 whitespace-nowrap text-center text-xs">
                      <div className="inline-flex flex-col items-center gap-1">
                        <span className={`px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest ${roleConfigs[user.role || 'Null'].bg} ${roleConfigs[user.role || 'Null'].text} border ${roleConfigs[user.role || 'Null'].border}`}>
                          {user.role || "Citizen"}
                        </span>
                        {user.iinsafId && (
                          <div className="text-gray-400 text-[9px] font-mono font-bold">
                            {user.iinsafId}
                          </div>
                        )}
                      </div>
                    </td>

                    <td className="px-6 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 bg-emerald-50 text-emerald-600 rounded-lg flex items-center justify-center text-[10px]">
                          <FaWallet />
                        </div>
                        <p className="text-sm font-black text-[#1e293b]">₹{(user.walletBalance || 0).toFixed(2)}</p>
                      </div>
                    </td>

                    <td className="px-6 py-3 whitespace-nowrap text-xs">
                      <div className="flex flex-col">
                        <span className="font-bold text-[#475569]">{user.email || 'N/A'}</span>
                        <span className="text-[10px] text-gray-400 font-medium">{user.mobile || 'N/A'}</span>
                      </div>
                    </td>

                    <td className="px-6 py-3 whitespace-nowrap">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleEditClick(user)}
                          className="w-8 h-8 flex items-center justify-center bg-gray-50 text-[#475569] hover:bg-blue-600 hover:text-white rounded-lg transition-all border border-gray-100"
                          title="Manage"
                        >
                          <FaEdit size={12} />
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user._id)}
                          className="w-8 h-8 flex items-center justify-center bg-red-50 text-red-500 hover:bg-red-500 hover:text-white rounded-lg transition-all border border-red-50"
                          title="Delete"
                        >
                          <FaTrash size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Improved Pagination Design */}
        {totalPages > 1 && (
          <div className="p-8 bg-[#f8fafc] border-t border-[#f1f5f9] flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-[#64748b] font-bold">
              Showing <span className="text-[#1e293b]">{indexOfFirstItem + 1}</span> - <span className="text-[#1e293b]">{Math.min(indexOfLastItem, filteredUsers.length)}</span> of <span className="text-[#1e293b] font-black">{filteredUsers.length}</span> entities
            </p>

            <div className="flex items-center gap-2 bg-white p-1.5 rounded-2xl border border-[#e2e8f0] shadow-sm">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="w-10 h-10 flex items-center justify-center text-[#64748b] hover:bg-gray-50 disabled:opacity-30 transition-all rounded-xl"
              >
                <FaChevronLeft size={12} />
              </button>

              <div className="flex items-center gap-1.5 px-2">
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                  .map((p, i, arr) => (
                    <React.Fragment key={p}>
                      {i > 0 && arr[i - 1] !== p - 1 && <span className="text-gray-300">...</span>}
                      <button
                        onClick={() => setCurrentPage(p)}
                        className={`w-10 h-10 flex items-center justify-center rounded-xl text-xs font-black transition-all ${currentPage === p
                          ? "bg-blue-600 text-white shadow-lg shadow-blue-500/20 scale-110"
                          : "text-gray-500 hover:bg-gray-50 hover:text-blue-600"
                          }`}
                      >
                        {p}
                      </button>
                    </React.Fragment>
                  ))
                }
              </div>

              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="w-10 h-10 flex items-center justify-center text-[#64748b] hover:bg-gray-50 disabled:opacity-30 transition-all rounded-xl"
              >
                <FaChevronRight size={12} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Premium Edit Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#0f172a]/80 backdrop-blur-xl animate-in fade-in duration-300">
          <div className="bg-white rounded-[3rem] shadow-2xl w-full max-w-3xl overflow-hidden border border-white/20 animate-in zoom-in-95 duration-300">
            <div className="relative h-40 bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-800 p-8 flex items-end">
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="absolute top-6 right-6 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white backdrop-blur-md transition-all active:scale-95"
              >
                <FaTimes />
              </button>
              <div className="flex items-center gap-5">
                <div className="w-24 h-24 rounded-[2rem] bg-white p-1.5 shadow-2xl">
                  <div className={`w-full h-full rounded-[1.7rem] bg-${roleConfigs[editingUser?.role || 'Null'].color}-500 flex items-center justify-center text-white text-3xl font-black`}>
                    {editingUser?.name?.charAt(0) || <FaUser />}
                  </div>
                </div>
                <div>
                  <h2 className="text-2xl font-black text-white uppercase tracking-tight">{editingUser?.name || "Modifying Entity"}</h2>
                  <p className="text-white/60 text-xs font-bold uppercase tracking-widest">{editingUser?.role || 'Citizen'} • ID: {editingUser?._id?.slice(-8)}</p>
                </div>
              </div>
            </div>

            <form onSubmit={handleUpdateUser} className="p-10 max-h-[60vh] overflow-y-auto custom-scrollbar bg-white">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">

                {/* Identification Section */}
                <div className="md:col-span-2 flex items-center gap-2 mb-2 pb-2 border-b border-gray-100">
                  <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                  <h4 className="text-[10px] font-black uppercase text-gray-400 tracking-widest">Core Profile</h4>
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-black text-gray-500 uppercase ml-1">Full Identity</label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="w-full px-5 py-3.5 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-blue-500/50 outline-none transition-all text-sm font-bold text-gray-700"
                    placeholder="Enter full name"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-black text-gray-500 uppercase ml-1">Email Interface</label>
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    className="w-full px-5 py-3.5 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-blue-500/50 outline-none transition-all text-sm font-bold text-gray-700"
                    placeholder="example@iinsaf.com"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-black text-gray-500 uppercase ml-1">Mobile Access</label>
                  <input
                    type="text"
                    value={editForm.mobile}
                    onChange={(e) => setEditForm({ ...editForm, mobile: e.target.value })}
                    className="w-full px-5 py-3.5 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-blue-500/50 outline-none transition-all text-sm font-bold text-gray-700"
                    placeholder="+91 XXXXX XXXXX"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-black text-gray-500 uppercase ml-1">Ecosystem Role</label>
                  <select
                    value={editForm.role}
                    onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                    className="w-full px-5 py-3.5 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-blue-500/50 outline-none transition-all text-sm font-bold text-gray-700 appearance-none cursor-pointer"
                  >
                    <option value="Advertiser">Advertiser</option>
                    <option value="Reporter">Reporter</option>
                    <option value="Influencer">Influencer</option>
                  </select>
                </div>

                {/* Geography Section */}
                <div className="md:col-span-2 flex items-center gap-2 mt-4 mb-2 pb-2 border-b border-gray-100">
                  <div className="w-2 h-2 rounded-full bg-pink-500"></div>
                  <h4 className="text-[10px] font-black uppercase text-gray-400 tracking-widest">Geographic Presence</h4>
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-black text-gray-500 uppercase ml-1">State / Province</label>
                  <input
                    type="text"
                    value={editForm.state}
                    onChange={(e) => setEditForm({ ...editForm, state: e.target.value })}
                    className="w-full px-5 py-3.5 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-blue-500/50 outline-none transition-all text-sm font-bold text-gray-700"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-black text-gray-500 uppercase ml-1">District / City</label>
                  <input
                    type="text"
                    value={editForm.city}
                    onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                    className="w-full px-5 py-3.5 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-blue-500/50 outline-none transition-all text-sm font-bold text-gray-700"
                  />
                </div>

                <div className="md:col-span-2 space-y-1">
                  <label className="text-[10px] font-black text-gray-500 uppercase ml-1">Full Residency Address</label>
                  <textarea
                    rows="2"
                    value={editForm.residenceaddress}
                    onChange={(e) => setEditForm({ ...editForm, residenceaddress: e.target.value })}
                    className="w-full px-5 py-3.5 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-blue-500/50 outline-none transition-all text-sm font-bold text-gray-700"
                  ></textarea>
                </div>

                {/* Security Section */}
                <div className="md:col-span-2 flex items-center gap-2 mt-4 mb-2 pb-2 border-b border-gray-100">
                  <div className="w-2 h-2 rounded-full bg-red-500"></div>
                  <h4 className="text-[10px] font-black uppercase text-gray-400 tracking-widest">Security Override</h4>
                </div>

                <div className="md:col-span-2 mb-4">
                  <label className="text-[10px] font-black text-gray-500 uppercase ml-1">Reset Password Protocol</label>
                  <input
                    type="password"
                    value={editForm.password}
                    onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                    className="w-full px-5 py-3.5 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-red-500/50 outline-none transition-all text-sm font-bold text-gray-700"
                    placeholder="Enter new terminal password"
                  />
                  <p className="text-[9px] text-gray-400 font-bold mt-2 uppercase tracking-tighter">System will only update if characters are entered above.</p>
                </div>
              </div>
            </form>

            <div className="p-8 bg-gray-50 flex gap-4 justify-between items-center shadow-inner">
              <button
                type="button"
                onClick={() => setIsEditModalOpen(false)}
                className="text-sm text-[#64748b] font-black uppercase tracking-widest hover:text-[#1e293b] transition-colors"
                disabled={updateLoading}
              >
                Discard Changes
              </button>
              <button
                onClick={handleUpdateUser}
                disabled={updateLoading}
                className="px-10 py-4 bg-blue-600 hover:bg-blue-700 text-white font-black rounded-2xl text-sm transition-all flex items-center gap-3 shadow-xl shadow-blue-500/25 active:scale-95 disabled:opacity-50"
              >
                {updateLoading ? (
                  <div className="w-5 h-5 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <FaSave />
                )}
                Commit Updates
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllUser;
